import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import { motion } from "motion/react"
import {
  ArrowRight,
  CheckCircle,
  Building07,
  ClipboardCheck,
  File02,
  Settings01,
  Users01,
  Car01,
} from "@untitledui/icons"

import { WelcomeShell, WelcomeCard } from "@/components/welcome/welcome-shell"
import { PrimaryButton } from "@/components/onboarding/onboarding-shell"
import {
  getRoleTourEntry,
  type TipIcon,
} from "@/lib/welcome/role-tour-registry"
import { readWelcomeNavState } from "@/lib/welcome/nav-state"
import { cx } from "@/lib/utils/cx"

const TIP_ICON: Record<TipIcon, React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>> = {
  estimate: ClipboardCheck,
  team: Users01,
  settings: Settings01,
  dashboard: Building07,
  report: File02,
  vehicle: Car01,
}

/**
 * Step 3 — role-aware tour. Reads team/role from `location.state` (set by
 * /invites/:uuid after a successful accept and forwarded through the
 * preceding welcome pages). Looks up role/team-type-specific copy from the
 * registry and routes to a role-appropriate landing screen on CTA click.
 *
 * No server-side "mark welcomed" call: the real BE doesn't track that
 * field today, so we don't fake the round-trip. The tour is one-shot per
 * invite acceptance; if the user comes back to the URL directly later we
 * bounce them home (no nav state = no welcome context).
 */
export default function WelcomeTourPage() {
  const location = useLocation()
  const navigate = useNavigate()

  const navState = readWelcomeNavState(location.state)

  const [submitting, setSubmitting] = useState<"primary" | "secondary" | null>(null)

  useEffect(() => {
    if (!navState) {
      navigate("/", { replace: true })
    }
  }, [navState, navigate])

  if (!navState) return null

  const entry = getRoleTourEntry(navState.teamType, navState.role)

  const finishAndNavigate = (
    target: { label: string; to: string },
    which: "primary" | "secondary",
  ) => {
    if (submitting != null) return
    setSubmitting(which)
    // Replace so the back button doesn't drop the user back into the tour
    // (which would re-render with the same state and re-navigate forward,
    // creating a navigation loop the user can't escape with the Back button).
    navigate(target.to, { replace: true })
  }

  return (
    <WelcomeShell
      step="tour"
      team={{ name: navState.teamName, role: navState.role }}
    >
      <WelcomeCard
        eyebrow="Step 3 of 3"
        title={entry.headline}
        subtitle={entry.body}
        hero={<TourHero teamName={navState.teamName} role={navState.role} />}
      >
        <div className="space-y-6">
          {entry.tips && entry.tips.length > 0 && (
            <ul className="space-y-3">
              {entry.tips.map((tip, i) => {
                const Icon = TIP_ICON[tip.icon]
                return (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 * i, duration: 0.3 }}
                    className="flex items-start gap-3 rounded-2xl border border-zinc-200/70 bg-white px-4 py-3"
                  >
                    <span className="mt-0.5 size-8 rounded-xl bg-emerald-50 grid place-items-center shrink-0">
                      <Icon className="size-4 text-emerald-600" aria-hidden />
                    </span>
                    <span className="text-[14px] text-zinc-700 leading-relaxed">{tip.text}</span>
                  </motion.li>
                )
              })}
            </ul>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 pt-2">
            {entry.secondaryCTA && (
              <button
                type="button"
                onClick={() => finishAndNavigate(entry.secondaryCTA!, "secondary")}
                disabled={submitting != null}
                className={cx(
                  "h-12 px-6 rounded-xl border border-zinc-200 bg-white text-zinc-700 text-[14px] font-semibold",
                  "hover:bg-zinc-50 hover:border-zinc-300 transition-colors",
                  "disabled:opacity-40 disabled:cursor-not-allowed",
                )}
              >
                {submitting === "secondary" ? (
                  <span className="size-4 border-2 border-zinc-400/40 border-t-zinc-700 rounded-full animate-spin inline-block" />
                ) : (
                  entry.secondaryCTA.label
                )}
              </button>
            )}
            <div className="flex-1">
              <PrimaryButton
                type="button"
                onClick={() => finishAndNavigate(entry.primaryCTA, "primary")}
                loading={submitting === "primary"}
                disabled={submitting === "secondary"}
                size="wide"
                variant="dark"
              >
                <span className="inline-flex items-center gap-2">
                  {entry.primaryCTA.label}
                  <ArrowRight className="size-4" aria-hidden />
                </span>
              </PrimaryButton>
            </div>
          </div>
        </div>
      </WelcomeCard>
    </WelcomeShell>
  )
}

function TourHero({ teamName, role }: { teamName: string; role: string }) {
  return (
    <div className="w-full max-w-sm space-y-5">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600">
          You're in
        </p>
        <h3 className="mt-2 text-[26px] font-bold text-zinc-900 tracking-tight leading-tight">
          {teamName}
        </h3>
        <p className="mt-1 text-[14px] text-zinc-500">as {role}</p>
      </div>

      <div className="rounded-2xl border border-zinc-200/70 bg-white/70 backdrop-blur-sm p-5 space-y-3">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
          <CheckCircle className="size-3.5 text-emerald-500" aria-hidden />
          Set up
        </div>
        <ul className="space-y-2.5 text-[13px]">
          {[
            "Account claimed",
            "Name confirmed",
            "Role-specific quick start ready",
          ].map((line, i) => (
            <li key={i} className="flex items-start gap-2.5 text-zinc-600">
              <CheckCircle className="mt-0.5 size-4 text-emerald-500 shrink-0" aria-hidden />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
